<template>
  <div class="asset-performance">
    <h2>{{ t('simulation.assetPerformance.title') }}</h2>
    
    <!-- 资产列表区域 -->
    <div class="asset-section">
      <div class="asset-header">
        <h4>{{ t('simulation.assetPerformance.assetList.heading', { count: assetList.length }) }}</h4>
        <div class="asset-pagination-controls">
          <select v-model="assetPageSize" @change="resetAssetPage">
            <option 
              v-for="size in assetPageSizeOptions" 
              :key="`asset-page-size-${size}`" 
              :value="size"
            >
              {{ t('simulation.assetPerformance.pagination.assetsPageSize', { count: size }) }}
            </option>
          </select>
        </div>
      </div>
      
      <div class="asset-tabs">
        <!-- 全局按钮 -->
        <button 
          @click="selectGlobalView"
          :class="['asset-tab', { active: isGlobalView }]"
          class="global-tab">
          {{ t('simulation.assetPerformance.tabs.global') }}
        </button>
        
        <!-- 股票标签 - 分页显示 -->
        <button 
          v-for="asset in paginatedAssetList" 
          :key="asset.id"
          @click="selectAsset(asset)"
          :class="['asset-tab', { active: selectedAsset?.id === asset.id && !isGlobalView }]">
          {{ asset.name || asset.code }}
        </button>
      </div>
      
      <!-- 资产分页器 -->
      <div v-if="totalAssetPages > 1" class="pagination">
        <button 
          @click="assetCurrentPage = 1" 
          :disabled="assetCurrentPage === 1"
          class="pagination-btn">
          {{ t('common.first') }}
        </button>
        <button 
          @click="assetCurrentPage--" 
          :disabled="assetCurrentPage === 1"
          class="pagination-btn">
          {{ t('common.previous') }}
        </button>
        
        <span class="pagination-info">
          {{ t('simulation.assetPerformance.pagination.info', { current: assetCurrentPage, total: totalAssetPages }) }}
        </span>
        
        <button 
          @click="assetCurrentPage++" 
          :disabled="assetCurrentPage === totalAssetPages"
          class="pagination-btn">
          {{ t('common.next') }}
        </button>
        <button 
          @click="assetCurrentPage = totalAssetPages" 
          :disabled="assetCurrentPage === totalAssetPages"
          class="pagination-btn">
          {{ t('common.last') }}
        </button>
      </div>
    </div>
    
    <!-- 全局视图 -->
    <div v-if="isGlobalView" class="global-view">
      <!-- 移除整个统计信息框 -->
      
      <!-- 全局交易明细表格 -->
      <div v-if="allTransactions.length > 0" class="transaction-details">
        <div class="transaction-header">
          <h4>{{ t('simulation.assetPerformance.global.title', { count: allTransactions.length }) }}</h4>
          <div class="transaction-pagination-controls">
            <select v-model="globalTransactionPageSize" @change="resetGlobalTransactionPage">
              <option
                v-for="size in recordPageSizeOptions"
                :key="`global-page-size-${size}`"
                :value="size"
              >
                {{ t('simulation.assetPerformance.pagination.recordsPageSize', { count: size }) }}
              </option>
            </select>
          </div>
        </div>
        <div class="transaction-table">
          <table>
            <thead>
              <tr>
                <th @click="sortBy('asset_name')">{{ t('simulation.assetPerformance.tableHeaders.asset') }}</th>
                <th @click="sortBy('open_day')">{{ t('simulation.assetPerformance.tableHeaders.openDate') }}</th>
                <th @click="sortBy('open_price')">{{ t('simulation.assetPerformance.tableHeaders.openPrice') }}</th>
                <th @click="sortBy('close_day')">{{ t('simulation.assetPerformance.tableHeaders.closeDate') }}</th>
                <th @click="sortBy('close_price')">{{ t('simulation.assetPerformance.tableHeaders.closePrice') }}</th>
                <th @click="sortBy('order_return')">{{ t('simulation.assetPerformance.tableHeaders.return') }}</th>
                <th @click="sortBy('holding_days')">{{ t('simulation.assetPerformance.tableHeaders.holdingDays') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(tx, index) in paginatedGlobalTransactions" :key="index">
                <td>{{ tx.asset_name || tx.name || tx.code || tx.ticker || getAssetNameById(tx.asset_id) }}</td>
                <td>{{ formatDate(tx.open_day) }}</td>
                <td :class="{ 'price-up': tx.direction === 1, 'price-down': tx.direction === -1 }">
                  {{ formatPrice(tx.open_price) }}
                </td>
                <td>{{ formatDate(tx.close_day) || statusLabels.value.open }}</td>
                <td :class="getProfitClass(tx.order_return)">
                  {{ formatPrice(tx.close_price) }}
                </td>
                <td :class="getProfitClass(tx.order_return)">
                  {{ formatReturn(tx.order_return) }}
                </td>
                <td>{{ calculateHoldingDays(tx.open_day, tx.close_day) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- 全局交易分页器 -->
        <div v-if="totalGlobalTransactionPages > 1" class="pagination">
          <button 
            @click="globalTransactionCurrentPage = 1" 
            :disabled="globalTransactionCurrentPage === 1"
            class="pagination-btn">
            {{ t('common.first') }}
          </button>
          <button 
            @click="globalTransactionCurrentPage--" 
            :disabled="globalTransactionCurrentPage === 1"
            class="pagination-btn">
            {{ t('common.previous') }}
          </button>
          
          <span class="pagination-info">
            {{ t('simulation.assetPerformance.pagination.info', { current: globalTransactionCurrentPage, total: totalGlobalTransactionPages }) }}
          </span>
          
          <button 
            @click="globalTransactionCurrentPage++" 
            :disabled="globalTransactionCurrentPage === totalGlobalTransactionPages"
            class="pagination-btn">
            {{ t('common.next') }}
          </button>
          <button 
            @click="globalTransactionCurrentPage = totalGlobalTransactionPages" 
            :disabled="globalTransactionCurrentPage === totalGlobalTransactionPages"
            class="pagination-btn">
            {{ t('common.last') }}
          </button>
        </div>
      </div>
      <div v-else class="no-data-message">
        {{ t('simulation.assetPerformance.global.empty') }}
      </div>
    </div>
    
    <!-- 单个股票视图 -->
    <div v-else-if="selectedAsset" class="asset-detail">
      <div class="asset-info">
        <h3>{{ t('simulation.assetPerformance.detail.title', { name: selectedAsset.name || selectedAsset.code }) }}</h3>
        <div class="asset-stats">
          <div class="stat-item">
            <label>{{ t('simulation.assetPerformance.detail.tradeCountLabel') }}</label>
            <span>{{ getAssetTransactionCount(selectedAsset.code) }}</span>
          </div>
          <div class="stat-item">
            <label>{{ t('simulation.assetPerformance.detail.currentHoldingLabel') }}</label>
            <span>{{ getAssetHolding(selectedAsset.code) }}</span>
          </div>
        </div>
      </div>
      
      <!-- K-line Chart -->
      <div class="kline-section">
        <div class="kline-header">
          <h4>{{ t('simulation.assetPerformance.kline.title', { name: selectedAsset.name || selectedAsset.code }) }}</h4>
          <div class="header-controls">
            <button @click="loadKLineData" :disabled="isLoadingKLine" class="btn btn-sm">
              {{ isLoadingKLine ? t('common.loading') : t('simulation.assetPerformance.kline.refresh') }}
            </button>
          </div>
        </div>
        <div class="chart-legend" v-if="getAssetTransactions(selectedAsset.code).length > 0">
          <div class="legend-item">
            <span class="legend-marker buy-marker">▲</span>
            <span>{{ t('simulation.assetPerformance.kline.legend.buy') }}</span>
          </div>
          <div class="legend-item">
            <span class="legend-marker sell-marker">▼</span>
            <span>{{ t('simulation.assetPerformance.kline.legend.sell') }}</span>
          </div>
        </div>
        <div v-if="kLineChartOptions" class="chart-container">
          <DataChart :key="selectedAsset?.id" :options="kLineChartOptions" />
          <div class="transaction-summary" v-if="getAssetTransactions(selectedAsset.code).length > 0">
            <div class="summary-item">
              <span class="buy-indicator">▲</span>
              <span>{{ t('simulation.assetPerformance.detail.buyCount', { count: getAssetTransactions(selectedAsset.code).filter(tx => tx.open_day && tx.open_price).length }) }}</span>
            </div>
            <div class="summary-item">
              <span class="sell-indicator">▼</span>
              <span>{{ t('simulation.assetPerformance.detail.sellCount', { count: getAssetTransactions(selectedAsset.code).filter(tx => tx.close_day && tx.close_price).length }) }}</span>
            </div>
            <div class="summary-item">
              <span class="profit-indicator">💰</span>
              <span>{{ t('simulation.assetPerformance.detail.averageReturnLabel') }}: {{ formatReturn(getAverageReturnForAsset(selectedAsset.code)) }}</span>
            </div>
          </div>
        </div>
        <div v-else-if="isLoadingKLine" class="loading-message">
          {{ t('simulation.assetPerformance.kline.loadingData') }}
        </div>
        <div v-else-if="kLineError" class="error-message">
          {{ kLineError }}
        </div>
        <div v-else class="no-data-message">
          {{ t('simulation.assetPerformance.kline.emptyPrompt') }}
        </div>
      </div>

      <!-- Technical Indicators -->
      <TechnicalIndicators 
        v-if="kLineData.length > 0"
        :kLineData="kLineData"
        :assetCode="selectedAsset.code"
        @loading-change="onIndicatorLoadingChange"
      />
      
      <!-- Transaction Details Table -->
      <div v-if="selectedAsset && getAssetTransactions(selectedAsset.code).length > 0" class="transaction-details">
        <div class="transaction-header">
          <h4>{{ t('simulation.assetPerformance.detail.transactionTitle', { name: selectedAsset.name || selectedAsset.code, count: getAssetTransactions(selectedAsset.code).length }) }}</h4>
          <div class="transaction-pagination-controls">
            <select v-model="assetTransactionPageSize" @change="resetAssetTransactionPage">
              <option
                v-for="size in recordPageSizeOptions"
                :key="`asset-page-size-${size}`"
                :value="size"
              >
                {{ t('simulation.assetPerformance.pagination.recordsPageSize', { count: size }) }}
              </option>
            </select>
          </div>
        </div>
        <div class="transaction-table">
          <table>
            <thead>
              <tr>
                <th @click="sortBy('open_day')">{{ t('simulation.assetPerformance.tableHeaders.openDate') }}</th>
                <th @click="sortBy('open_price')">{{ t('simulation.assetPerformance.tableHeaders.openPrice') }}</th>
                <th @click="sortBy('close_day')">{{ t('simulation.assetPerformance.tableHeaders.closeDate') }}</th>
                <th @click="sortBy('close_price')">{{ t('simulation.assetPerformance.tableHeaders.closePrice') }}</th>
                <th @click="sortBy('order_return')">{{ t('simulation.assetPerformance.tableHeaders.return') }}</th>
                <th @click="sortBy('holding_days')">{{ t('simulation.assetPerformance.tableHeaders.holdingDays') }}</th>
                <th>{{ t('simulation.assetPerformance.tableHeaders.status') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(tx, index) in paginatedAssetTransactions" :key="index">
                <td>{{ formatDate(tx.open_day) }}</td>
                <td :class="{ 'price-up': tx.direction === 1, 'price-down': tx.direction === -1 }">
                  {{ formatPrice(tx.open_price) }}
                </td>
                <td>{{ formatDate(tx.close_day) || statusLabels.value.open }}</td>
                <td :class="getProfitClass(tx.order_return)">
                  {{ formatPrice(tx.close_price) }}
                </td>
                <td :class="getProfitClass(tx.order_return)">
                  {{ formatReturn(tx.order_return) }}
                </td>
                <td>{{ calculateHoldingDays(tx.open_day, tx.close_day) }}</td>
                <td>
                  <span :class="statusClass(tx.close_day)">
                    {{ tx.close_day ? statusLabels.value.closed : statusLabels.value.open }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- 资产交易分页器 -->
        <div v-if="totalAssetTransactionPages > 1" class="pagination">
          <button 
            @click="assetTransactionCurrentPage = 1" 
            :disabled="assetTransactionCurrentPage === 1"
            class="pagination-btn">
            {{ t('common.first') }}
          </button>
          <button 
            @click="assetTransactionCurrentPage--" 
            :disabled="assetTransactionCurrentPage === 1"
            class="pagination-btn">
            {{ t('common.previous') }}
          </button>
          
          <span class="pagination-info">
            {{ t('simulation.assetPerformance.pagination.info', { current: assetTransactionCurrentPage, total: totalAssetTransactionPages }) }}
          </span>
          
          <button 
            @click="assetTransactionCurrentPage++" 
            :disabled="assetTransactionCurrentPage === totalAssetTransactionPages"
            class="pagination-btn">
            {{ t('common.next') }}
          </button>
          <button 
            @click="assetTransactionCurrentPage = totalAssetTransactionPages" 
            :disabled="assetTransactionCurrentPage === totalAssetTransactionPages"
            class="pagination-btn">
            {{ t('common.last') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { getAssetKLineData as apiGetAssetKLineData } from '@/services/api.js';
import DataChart from '@/components/DataChart.vue';
import TechnicalIndicators from './TechnicalIndicators.vue';

const props = defineProps({
  assetList: {
    type: Array,
    default: () => []
  },
  transactionIds: {
    type: Array,
    default: () => []
  },
  holdingsOnCompletion: {
    type: Array,
    default: () => []
  },
  simulationParameters: {
    type: Object,
    default: () => ({})
  }
});

const { t, locale } = useI18n();

const assetPageSizeOptions = [20, 50, 100];
const recordPageSizeOptions = [50, 100, 200];

// 修改默认状态，让全局视图为默认选项
const selectedAsset = ref(null);
const isGlobalView = ref(true); // 改为true，默认显示全局视图
const kLineData = ref([]);
const isLoadingKLine = ref(false);
const kLineError = ref(null);

// 分页相关响应式数据
const assetCurrentPage = ref(1);
const assetPageSize = ref(50);

// 全局交易分页
const globalTransactionCurrentPage = ref(1);
const globalTransactionPageSize = ref(100);

// 资产交易分页
const assetTransactionCurrentPage = ref(1);
const assetTransactionPageSize = ref(100);

const statusLabels = computed(() => ({
  open: t('simulation.assetPerformance.status.open'),
  closed: t('simulation.assetPerformance.status.closed')
}));

const holdingLabels = computed(() => ({
  none: t('simulation.assetPerformance.holding.none'),
  long: t('simulation.assetPerformance.holding.long'),
  short: t('simulation.assetPerformance.holding.short')
}));

// 全局视图相关计算属性
const allTransactions = computed(() => {
  if (!props.transactionIds || !Array.isArray(props.transactionIds)) return [];
  return props.transactionIds;
});

// 资产分页计算属性
const totalAssetPages = computed(() => {
  return Math.ceil(props.assetList.length / assetPageSize.value);
});

const paginatedAssetList = computed(() => {
  const start = (assetCurrentPage.value - 1) * assetPageSize.value;
  const end = start + parseInt(assetPageSize.value);
  return props.assetList.slice(start, end);
});

// 全局交易分页计算属性
const totalGlobalTransactionPages = computed(() => {
  return Math.ceil(sortedGlobalTransactions.value.length / globalTransactionPageSize.value);
});

const paginatedGlobalTransactions = computed(() => {
  const start = (globalTransactionCurrentPage.value - 1) * globalTransactionPageSize.value;
  const end = start + parseInt(globalTransactionPageSize.value);
  return sortedGlobalTransactions.value.slice(start, end);
});

// 资产交易分页计算属性
const totalAssetTransactionPages = computed(() => {
  return Math.ceil(sortedTransactions.value.length / assetTransactionPageSize.value);
});

const paginatedAssetTransactions = computed(() => {
  const start = (assetTransactionCurrentPage.value - 1) * assetTransactionPageSize.value;
  const end = start + parseInt(assetTransactionPageSize.value);
  return sortedTransactions.value.slice(start, end);
});

// Define loadKLineData function first
const loadKLineData = async () => {
  if (!selectedAsset.value || !selectedAsset.value.id) {
    console.log('Cannot load K-line data: no selected asset or asset ID');
    return;
  }
  
  console.log('Loading K-line data for asset:', selectedAsset.value.code, 'ID:', selectedAsset.value.id);
  
  isLoadingKLine.value = true;
  kLineError.value = null;
  
  try {
    const startDate = props.simulationParameters?.start_date;
    const endDate = props.simulationParameters?.end_date;
    
    console.log('Date range:', startDate, 'to', endDate);
    
    const response = await apiGetAssetKLineData(selectedAsset.value.id, startDate, endDate);
    if (response && Array.isArray(response)) {
      response.sort((a, b) => new Date(a.date) - new Date(b.date));
    }
    kLineData.value = response || [];
    console.log('K-line data loaded:', kLineData.value.length, 'points');
  } catch (err) {
    console.error('Error loading K-line data:', err);
    kLineError.value = t('simulation.assetPerformance.errors.loadKLine');
    kLineData.value = [];
  } finally {
    isLoadingKLine.value = false;
  }
};

// 选择全局视图
const selectGlobalView = () => {
  isGlobalView.value = true;
  selectedAsset.value = null;
  kLineData.value = [];
  kLineError.value = null;
};

const selectAsset = (asset) => {
  console.log('Selecting asset:', asset.code);
  
  // 如果点击的是同一个股票，不做任何操作，保持当前状态
  if (selectedAsset.value && selectedAsset.value.id === asset.id && !isGlobalView.value) {
    console.log('Same asset selected, keeping current state');
    return;
  }
  
  isGlobalView.value = false; // 退出全局视图
  kLineData.value = [];
  kLineError.value = null;
  selectedAsset.value = asset;
  // 重置资产交易分页
  assetTransactionCurrentPage.value = 1;
};

// 根据asset_id获取股票名称
const getAssetNameById = (assetId) => {
  if (!assetId) return t('common.notAvailable');
  const asset = props.assetList.find(a => a.id === assetId);
  return asset ? (asset.name || asset.code) : t('common.notAvailable');
};

// Watch for asset list changes and set default
watch(() => props.assetList, (newAssetList) => {
  // 不再自动选择第一个股票，保持全局视图为默认
  if (newAssetList.length > 0 && !selectedAsset.value && !isGlobalView.value) {
    selectedAsset.value = newAssetList[0];
  }
}, { immediate: true });

// Watch for asset selection changes
watch(selectedAsset, async (newAsset, oldAsset) => {
  console.log('Asset selection changed:', { newAsset, oldAsset });
  
  // 如果切换到全局视图或者没有选择资产，不加载数据
  if (!newAsset || !newAsset.id || isGlobalView.value) {
    console.log('No asset selected or in global view, skipping K-line load');
    return;
  }
  
  console.log('Loading K-line data for asset:', newAsset.code);
  await nextTick(); // Ensure DOM is updated
  await loadKLineData();
}, { immediate: true });
const getAssetTransactionCount = (assetCode) => {
  if (!props.transactionIds || !Array.isArray(props.transactionIds)) return 0;
  return props.transactionIds.filter(tx => 
    tx && (tx.code === assetCode || tx.ticker === assetCode)
  ).length;
};

const getAssetHolding = (assetCode) => {
  if (!props.holdingsOnCompletion || !Array.isArray(props.holdingsOnCompletion)) {
    return holdingLabels.value.none;
  }

  const holding = props.holdingsOnCompletion.find(
    (h) => h && h.asset_code === assetCode
  );
  if (!holding || typeof holding.quantity !== 'number') {
    return holdingLabels.value.none;
  }

  return holding.quantity > 0
    ? t('simulation.assetPerformance.holding.long', { quantity: holding.quantity })
    : t('simulation.assetPerformance.holding.short', { quantity: Math.abs(holding.quantity) });
};

const getAssetTransactions = (assetCode) => {
  if (!props.transactionIds || !Array.isArray(props.transactionIds)) return [];
  return props.transactionIds.filter(tx => 
    tx && ((tx.code || tx.ticker) === assetCode || 
           (selectedAsset.value && tx.asset_id === selectedAsset.value.id))
  );
};

const formatPrice = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return t('common.notAvailable');
  }
  return `¥${Number(value).toFixed(2)}`;
};

const formatReturn = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return t('common.notAvailable');
  }
  return `${(Number(value) * 100).toFixed(2)}%`;
};

const getAverageReturnForAsset = (assetCode) => {
  const transactions = getAssetTransactions(assetCode).filter(
    (tx) => tx && tx.order_return !== undefined && tx.order_return !== null
  );
  if (!transactions.length) {
    return null;
  }
  const total = transactions.reduce((sum, tx) => sum + (Number(tx.order_return) || 0), 0);
  return total / transactions.length;
};

// Helper functions for formatting and calculations
const formatDate = (dateString) => {
  if (!dateString) return null;
  return dateString.includes('T') ? dateString.split('T')[0] : dateString;
};

const calculateHoldingDays = (openDay, closeDay, format = true) => {
  if (!openDay) return format ? t('common.notAvailable') : 0;

  const openDate = new Date(openDay);
  const closeDate = closeDay ? new Date(closeDay) : new Date();

  if (Number.isNaN(openDate.getTime()) || Number.isNaN(closeDate.getTime())) {
    return format ? t('common.notAvailable') : 0;
  }

  const diffTime = Math.abs(closeDate - openDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (!format) return diffDays;

  return closeDay
    ? t('simulation.assetPerformance.holdingDays.closed', { count: diffDays })
    : t('simulation.assetPerformance.holdingDays.open', { count: diffDays });
};

const getProfitClass = (orderReturn) => {
  if (orderReturn === undefined || orderReturn === null) return '';
  return orderReturn >= 0 ? 'profit-positive' : 'profit-negative';
};

const sortKey = ref('open_day');
const sortDirection = ref('desc');

const sortedTransactions = computed(() => {
  const transactions = getAssetTransactions(selectedAsset.value?.code);
  if (!transactions) return [];

  return [...transactions].sort((a, b) => {
    let valA = a[sortKey.value];
    let valB = b[sortKey.value];

    if (sortKey.value === 'open_day' || sortKey.value === 'close_day') {
      valA = new Date(valA);
      valB = new Date(valB);
    } else if (sortKey.value === 'order_return') {
      valA = valA || 0;
      valB = valB || 0;
    } else if (sortKey.value === 'holding_days') {
      valA = calculateHoldingDays(a.open_day, a.close_day, false);
      valB = calculateHoldingDays(b.open_day, b.close_day, false);
    }

    if (valA < valB) {
      return sortDirection.value === 'asc' ? -1 : 1;
    }
    if (valA > valB) {
      return sortDirection.value === 'asc' ? 1 : -1;
    }
    return 0;
  });
});

const sortedGlobalTransactions = computed(() => {
  if (!allTransactions.value) return [];

  return [...allTransactions.value].sort((a, b) => {
    let valA = a[sortKey.value];
    let valB = b[sortKey.value];

    if (sortKey.value === 'open_day' || sortKey.value === 'close_day') {
      valA = new Date(valA);
      valB = new Date(valB);
    } else if (sortKey.value === 'order_return') {
      valA = valA || 0;
      valB = valB || 0;
    } else if (sortKey.value === 'holding_days') {
      valA = calculateHoldingDays(a.open_day, a.close_day, false);
      valB = calculateHoldingDays(b.open_day, b.close_day, false);
    }

    if (valA < valB) {
      return sortDirection.value === 'asc' ? -1 : 1;
    }
    if (valA > valB) {
      return sortDirection.value === 'asc' ? 1 : -1;
    }
    return 0;
  });
});

const sortBy = (key) => {
  if (sortKey.value === key) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDirection.value = 'desc';
  }
};

const onIndicatorLoadingChange = (loading) => {
  // 处理技术指标加载状态变化
  console.log('Technical indicators loading:', loading);
};

// 分页重置方法
const resetAssetPage = () => {
  assetCurrentPage.value = 1;
};

const resetGlobalTransactionPage = () => {
  globalTransactionCurrentPage.value = 1;
};

const resetAssetTransactionPage = () => {
  assetTransactionCurrentPage.value = 1;
};

// 状态类方法
const statusClass = (closeDay) => {
  return closeDay ? 'status-closed' : 'status-open';
};
const kLineChartOptions = computed(() => {
  // Ensure locale changes trigger recomputation
  locale.value;

  if (!kLineData.value?.length || !selectedAsset.value) return null;

  try {
    const candlestickData = kLineData.value
      .filter((item) => item && typeof item === 'object')
      .map((item) => {
        const open = parseFloat(item.open_price) || 0;
        const close = parseFloat(item.close_price) || 0;
        const low = parseFloat(item.low_price) || 0;
        const high = parseFloat(item.high_price) || 0;

        const validOpen = Math.max(0.01, open);
        const validClose = Math.max(0.01, close);
        const validHigh = Math.max(validOpen, validClose, high, low);
        const validLow = Math.min(validOpen, validClose, low, high);

        return [validOpen, validClose, validLow, validHigh];
      })
      .filter((data) => data.every((price) => !Number.isNaN(price) && price >= 0.01));

    const dates = kLineData.value.filter((item) => item && item.date).map((item) => item.date);

    if (!candlestickData.length || !dates.length) {
      console.warn('No valid K-line data available for chart');
      return null;
    }

    const assetTransactions =
      props.transactionIds?.filter(
        (tx) =>
          tx &&
          (tx.asset_id === selectedAsset.value.id ||
            (tx.code || tx.ticker) === selectedAsset.value.code)
      ) || [];

    const buyMarkers = [];
    const sellMarkers = [];

    const chartTexts = {
      title: t('simulation.assetPerformance.kline.title', {
        name: selectedAsset.value.name || selectedAsset.value.code
      }),
      legendCandlestick: t('simulation.assetPerformance.kline.legend.candlestick'),
      seriesName: t('simulation.assetPerformance.kline.seriesName'),
      marker: {
        buyName: (price) =>
          t('simulation.assetPerformance.kline.markers.buyName', { price: formatPrice(price) }),
        buyDetails: ({ price, date, symbol }) =>
          t('simulation.assetPerformance.kline.markers.buyDetails', { price, date, symbol }),
        sellName: (price) =>
          t('simulation.assetPerformance.kline.markers.sellName', { price: formatPrice(price) }),
        sellDetails: ({ price, date, returnValue }) =>
          t('simulation.assetPerformance.kline.markers.sellDetails', {
            price,
            date,
            returnValue
          }),
        buyLabel: t('simulation.assetPerformance.kline.markers.buyLabel'),
        sellLabel: t('simulation.assetPerformance.kline.markers.sellLabel')
      },
      tooltip: {
        open: t('simulation.assetPerformance.kline.tooltip.open'),
        close: t('simulation.assetPerformance.kline.tooltip.close'),
        low: t('simulation.assetPerformance.kline.tooltip.low'),
        high: t('simulation.assetPerformance.kline.tooltip.high'),
        change: t('simulation.assetPerformance.kline.tooltip.change'),
        changePercent: t('simulation.assetPerformance.kline.tooltip.changePercent'),
        firstDay: t('simulation.assetPerformance.kline.tooltip.firstDay')
      }
    };

    assetTransactions.forEach((tx) => {
      if (!tx) return;

      if (tx.open_day && tx.open_price) {
        const openDate = tx.open_day.includes('T') ? tx.open_day.split('T')[0] : tx.open_day;
        const klinePoint = kLineData.value.find((d) => d.date === openDate);
        const yCoord =
          klinePoint && klinePoint.low_price ? klinePoint.low_price : tx.open_price;

        buyMarkers.push({
          name: chartTexts.marker.buyName(tx.open_price),
          coord: [openDate, yCoord],
          value: chartTexts.marker.buyDetails({
            price: formatPrice(tx.open_price),
            date: openDate,
            symbol: tx.code || selectedAsset.value.code
          }),
          symbolSize: 15
        });
      }

      if (tx.close_day && tx.close_price) {
        const closeDate = tx.close_day.includes('T') ? tx.close_day.split('T')[0] : tx.close_day;
        const klinePoint = kLineData.value.find((d) => d.date === closeDate);
        const yCoord =
          klinePoint && klinePoint.high_price ? klinePoint.high_price : tx.close_price;

        sellMarkers.push({
          name: chartTexts.marker.sellName(tx.close_price),
          coord: [closeDate, yCoord],
          value: chartTexts.marker.sellDetails({
            price: formatPrice(tx.close_price),
            date: closeDate,
            returnValue: formatReturn(tx.order_return)
          }),
          symbolSize: 15
        });
      }
    });

    return {
      title: {
        text: chartTexts.title,
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      legend: {
        data: [chartTexts.legendCandlestick],
        top: 30,
        itemGap: 20
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter(params) {
          if (!params || params.length === 0) return '';

          const data = params[0];
          if (data.seriesType === 'candlestick') {
            const dataIndex = data.dataIndex;
            const [open, close, low, high] = candlestickData[dataIndex];

            let changeMessage = chartTexts.tooltip.firstDay;
            if (dataIndex > 0) {
              const prevClose = candlestickData[dataIndex - 1][1];
              if (prevClose && prevClose !== 0) {
                const changeValue = close - prevClose;
                const changePercent = (changeValue / prevClose) * 100;
                const changeDisplay = `${changeValue > 0 ? '+' : ''}${changeValue.toFixed(2)}`;
                const percentDisplay = `${changePercent.toFixed(2)}%`;
                changeMessage = `${chartTexts.tooltip.change}: ${changeDisplay} (${percentDisplay})`;
              }
            }

            return `
              <div style="text-align: left;">
                <strong>${data.name}</strong><br/>
                ${chartTexts.tooltip.open}: ${formatPrice(open)}<br/>
                ${chartTexts.tooltip.close}: ${formatPrice(close)}<br/>
                ${chartTexts.tooltip.low}: ${formatPrice(low)}<br/>
                ${chartTexts.tooltip.high}: ${formatPrice(high)}<br/>
                ${changeMessage}
              </div>
            `;
          }
          return `${data.name}: ${data.value}`;
        }
      },
      xAxis: {
        type: 'category',
        data: dates,
        scale: true,
        boundaryGap: false,
        axisLine: { onZero: false },
        splitLine: { show: false },
        splitNumber: 20,
        min: 'dataMin',
        max: 'dataMax'
      },
      yAxis: {
        scale: true,
        splitArea: { show: true }
      },
      dataZoom: [
        { type: 'inside', start: 0, end: 100 },
        { type: 'slider', start: 0, end: 100, height: 20 }
      ],
      series: [
        {
          name: chartTexts.seriesName,
          type: 'candlestick',
          data: candlestickData,
          itemStyle: {
            color: '#ec0000',
            color0: '#00da3c',
            borderColor: '#8A0000',
            borderColor0: '#008F28'
          },
          markPoint: {
            symbol: 'circle',
            symbolSize: 15,
            data: [
              ...buyMarkers.map((marker) => ({
                ...marker,
                symbol: 'triangle',
                symbolSize: 20,
                itemStyle: {
                  color: '#00da3c',
                  borderColor: '#fff',
                  borderWidth: 2
                },
                label: {
                  show: true,
                  position: ['50%', '100%'],
                  formatter: chartTexts.marker.buyLabel,
                  fontSize: 10,
                  fontWeight: 'bold',
                  color: '#fff',
                  backgroundColor: '#00da3c',
                  padding: [2, 4],
                  borderRadius: 3
                }
              })),
              ...sellMarkers.map((marker) => ({
                ...marker,
                symbol: 'triangle',
                symbolRotate: 180,
                symbolSize: 22,
                itemStyle: {
                  color: '#ec0000',
                  borderColor: '#fff',
                  borderWidth: 3
                },
                label: {
                  show: true,
                  position: ['50%', '0%'],
                  formatter: chartTexts.marker.sellLabel,
                  fontSize: 11,
                  fontWeight: 'bold',
                  color: '#fff',
                  backgroundColor: '#ec0000',
                  padding: [3, 5],
                  borderRadius: 4
                }
              }))
            ]
          }
        }
      ]
    };
  } catch (error) {
    console.error('Error creating K-line chart options:', error);
    return null;
  }
});
</script>

<style scoped>
.asset-performance {
  margin-bottom: 32px;
}

.asset-performance h2 {
  margin: 0 0 20px 0;
  font-size: 20px;
  font-weight: 600;
  color: #262626;
}

.asset-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.asset-tab {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.asset-tab:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.asset-tab.active {
  background: #1890ff;
  border-color: #1890ff;
  color: white;
}

.asset-detail {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
}

.asset-info h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.asset-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-item label {
  font-size: 12px;
  color: #8c8c8c;
  font-weight: 500;
}

.stat-item span {
  font-size: 14px;
  color: #262626;
  font-weight: 600;
}

.kline-section {
  margin-top: 20px;
}

.kline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.kline-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  text-decoration: none;
  display: inline-block;
  background: #f5f5f5;
  border-color: #d9d9d9;
  color: #595959;
}

.btn:hover {
  background: #e6f7ff;
  border-color: #91d5ff;
  color: #1890ff;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 12px;
}

.chart-container {
  height: 400px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;
}

.loading-message,
.error-message,
.no-data-message {
  text-align: center;
  padding: 40px;
  color: #8c8c8c;
}

.error-message {
  color: #ff4d4f;
}

.transaction-summary {
  display: flex;
  gap: 20px;
  margin-top: 12px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 12px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.buy-indicator {
  color: #00da3c;
  font-size: 14px;
  font-weight: bold;
}

.sell-indicator {
  color: #ec0000;
  font-size: 14px;
  font-weight: bold;
}

.chart-legend {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 12px;
  border: 1px solid #e8e8e8;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.legend-marker {
  font-size: 14px;
  font-weight: bold;
}

.buy-marker {
  color: #00da3c;
}

.sell-marker {
  color: #ec0000;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.test-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #666;
  cursor: pointer;
}

.test-toggle input[type="checkbox"] {
  margin: 0;
}
.transaction-details {
  margin-top: 24px;
  border-top: 1px solid #e8e8e8;
  padding-top: 20px;
}

.transaction-details h4 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.transaction-table {
  overflow-x: auto;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
}

.transaction-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.transaction-table th,
.transaction-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #e8e8e8;
}

.transaction-table th {
  background: #fafafa;
  font-weight: 600;
  color: #262626;
}

.transaction-table tr:last-child td {
  border-bottom: none;
}

.transaction-table tr:hover {
  background: #f8f9fa;
}

.price-up {
  color: #00da3c;
  font-weight: 600;
}

.price-down {
  color: #ec0000;
  font-weight: 600;
}

.profit-positive {
  color: #00da3c;
  font-weight: 600;
}

.profit-negative {
  color: #ec0000;
  font-weight: 600;
}

.transaction-table td {
  color: #262626;
}
/* 移除全局按钮的紫色样式 */
.global-tab {
  /* 移除之前的紫色渐变样式 */
  background: transparent;
  color: inherit;
  font-weight: inherit;
}

.global-tab.active {
  /* 移除之前的紫色渐变样式 */
  background: #1890ff;
  color: white;
  box-shadow: none;
}

.global-tab:hover:not(.active) {
  /* 移除之前的紫色渐变样式 */
  background: #f0f0f0;
}

/* 移除不再需要的全局统计信息样式 */
.global-info {
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.global-info h3 {
  color: #262626;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 15px;
  text-align: center;
}

.global-stats {
  display: flex;
  justify-content: center;
  gap: 40px;
  flex-wrap: wrap;
}

.stat-item {
  text-align: center;
}

.stat-item label {
  display: block;
  color: #666;
  font-size: 14px;
  margin-bottom: 5px;
}

.stat-item span {
  display: block;
  color: #1890ff;
  font-size: 24px;
  font-weight: bold;
}

@media (max-width: 768px) {
  .global-stats {
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
}

/* 资产区域样式 */
.asset-section {
  margin-bottom: 30px;
}

.asset-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
}

.asset-header h4 {
  margin: 0;
  color: #333;
  font-size: 16px;
}

.asset-pagination-controls select {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  font-size: 14px;
}

/* 交易明细头部样式 */
.transaction-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
}

.transaction-header h4 {
  margin: 0;
  color: #333;
  font-size: 16px;
}

.transaction-pagination-controls select {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  font-size: 14px;
}

/* 分页器样式 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #e9ecef;
  border-color: #adb5bd;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f8f9fa;
}

.pagination-info {
  font-size: 14px;
  color: #666;
  margin: 0 10px;
}

/* 状态样式 */
.status-open {
  color: #ffc107;
  font-weight: 600;
}

.status-closed {
  color: #6c757d;
  font-weight: 600;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .global-stats {
    justify-content: center;
  }
  
  .global-stats .stat-item {
    min-width: 100px;
  }
  
  .asset-header,
  .transaction-header {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }
  
  .pagination {
    flex-wrap: wrap;
    gap: 5px;
  }
}
</style>
